import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

/**
 * Authentication Provider to manage global user state.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  // Initialize Auth State from Session Storage
  useEffect(() => {
    try {
      const savedUser = sessionStorage.getItem('ella_user');
      if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
        setUser(JSON.parse(savedUser));
      }
    } catch (e) {
      console.warn('[AuthContext] Session restore failed:', e.message);
      sessionStorage.removeItem('ella_user'); // Clean up bad data
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Persists user to auth state and session storage.
   */
  const login = (userData) => {
    setUser(userData);
    sessionStorage.setItem('ella_user', JSON.stringify(userData));
    // Tokens are also stored in session storage for session-only persistence
    if (userData.token) {
      sessionStorage.setItem('token', userData.token);
    }
    setIsAuthModalOpen(false); // Close modal on successful login
  };

  const openAuthModal = (mode = 'login') => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  const closeAuthModal = () => setIsAuthModalOpen(false);

  /**
   * Clears auth state and session storage.
   */
  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('ella_user');
    sessionStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout,
      isAuthModalOpen,
      authMode,
      openAuthModal,
      closeAuthModal
    }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook for accessing authentication context.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


